import { useTranslation } from 'react-i18next'
import {
  CrossIcon,
  CurecallButton,
  CurecallCopyButton,
  CurecallTooltip,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useState } from 'react'
import { TPatientInteropLog } from './interop-log.type'
import { TTestInteropMappingResponse } from './interop-mapping.type'
import { InteropTestMappingResultComponent } from './interop-test-mapping-result.component'
import { TPatient } from 'src/types/patient'
import { TProject } from 'src/types/project'
import { PatientService } from 'src/services/patient.service'
import { getErrorMessage } from 'src/utils/http.util'
import { useNotification } from 'src/providers/notification.provider'

export type TInteropLogActionsComponentProps = {
  interopLog: TPatientInteropLog | undefined
  patient: Pick<TPatient, 'id'>
  project: TProject
  onReplayed?: VoidFunction
}

export function InteropLogActionsComponent({
  interopLog,
  patient,
  project,
  onReplayed,
}: TInteropLogActionsComponentProps) {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const [testMappingResult, setTestMappingResult] = useState<TTestInteropMappingResponse>()
  const [testMappingError, setTestMappingError] = useState<string>()
  const [replayMappingError, setReplayMappingError] = useState<string>()

  if (!interopLog) {
    return
  }

  const onClickTestInteropLogMapping = async () => {
    try {
      const _testMappingResult = await PatientService.testInteropLogMapping(
        project.id,
        patient.id,
        interopLog?.id,
      )
      setTestMappingResult(_testMappingResult)
      setTestMappingError(undefined)
    } catch (error) {
      setTestMappingError(getErrorMessage(error))
      return
    }
  }

  const onReplayInterop = async () => {
    try {
      await PatientService.replayInteropLogMapping(interopLog.id)
      notify({
        title: t('common.actions.success'),
        message: t('components.patient.interop-logs.actions.success-replay-interop', {
          interopLogId: interopLog.id,
        }),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
      onReplayed?.()
    } catch (error) {
      setReplayMappingError(getErrorMessage(error))
      return
    }
  }

  return (
    <div className="c-m-1 c-rounded-lg c-grow c-bg-white c-px-2 c-py-1 c-border c-border-grey-500">
      <h5 className="c-text-center">{'Actions'}</h5>
      <div className="c-flex c-gap-2 c-items-center c-my-2">
        <CurecallTooltip content={t('common.actions.copy')} tooltipPosition="bottom-start">
          <CurecallCopyButton
            ariaLabelText={t('common.actions.copy')}
            data={JSON.stringify(interopLog.payload)}
          ></CurecallCopyButton>
        </CurecallTooltip>
        <CurecallTooltip
          content={t('components.patient.interop-logs.actions.tooltip')}
          tooltipPosition="bottom-start"
        >
          <CurecallButton size={ECurecallSize.SMALL} onClick={onClickTestInteropLogMapping}>
            {t('components.patient.interop-logs.actions.test-button')}
          </CurecallButton>
        </CurecallTooltip>
        {testMappingError ? (
          <div className="c-flex">
            {testMappingError === 'NO_MAPPING' ? (
              <>
                <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
                {t('components.patient.interop-logs.actions.no-mapping')}
              </>
            ) : (
              <>
                <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
                {testMappingError}
              </>
            )}
          </div>
        ) : null}

        <CurecallTooltip
          content={t('components.patient.interop-logs.actions.warning-replay-interop')}
          tooltipPosition="bottom-start"
        >
          <CurecallButton
            appearance={ECurecallButtonAppearance.ALERT}
            size={ECurecallSize.SMALL}
            onClick={onReplayInterop}
          >
            {t('components.patient.interop-logs.actions.replay-interop')}
          </CurecallButton>
        </CurecallTooltip>
        {replayMappingError && (
          <div className="c-flex">
            {replayMappingError === 'NO_MAPPING' ? (
              <>
                <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
                {t('components.patient.interop-logs.actions.no-mapping')}
              </>
            ) : (
              <>
                <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
                {replayMappingError}
              </>
            )}
          </div>
        )}
      </div>
      {!testMappingError && !!testMappingResult && (
        <InteropTestMappingResultComponent result={testMappingResult} />
      )}
    </div>
  )
}
